/**
 * vetRouteMap.js — Carte page ↔ URL du shell Mode Pro Vétérinaire.
 *
 * Track 2.x routeur vetpro, Lot V0+1 — MIROIR de routing/ownerRouteMap. Source
 * unique des chemins /vet/*. Établit l'enum VET_PAGES (solde dette
 * [vet-pro-pages-enum]) et le set valide (whitelist runtime, solde
 * [useVetProState-whitelist-pages]).
 *
 * COEXISTENCE V0+1 (miroir owner Lot 0+1) :
 *   - Pages SANS PARAM (VET_PAGE_TO_PATH) : routées, l'URL fait foi (deep-link).
 *   - Pages PROFONDES (DEEP_VET_PAGES) : encore pilotées par `vetProPage` (state
 *     useVetProState). Tant que vetProPage ∈ DEEP, le switch legacy de VetProRoot
 *     prime sur <Routes> (exactement comme NON_ROUTED_PAGES côté owner).
 *
 * Lot V2a : caseDetail + sessionView SONT ROUTÉES (`/vet/cases/:caseId{/sessions/
 * :sessionId}`, reload-by-id) → RETIRÉES de DEEP_VET_PAGES (6 → 4). Restent state
 * (Lot V2b) : horseSessions, horseSession, sessionGlobal, loanSession.
 */
import { matchPath } from 'react-router-dom';

// Enum des pages vetpro — TOUTES les valeurs valides de `vetProPage` (sans param
// + profondes). Remplace les strings flat éparpillés (anti-typo, [vet-pro-pages-enum]).
export const VET_PAGES = Object.freeze({
  // Sans paramètre — routées V0+1.
  HOME:      'home',
  NEW_CASE:  'newCase',
  HORSES:    'horses',
  NEW_HORSE: 'newHorse',
  SESSIONS:  'sessions',
  LOANS:     'loans',
  ACCOUNT:   'account',
  FEEDBACK:  'feedback',
  // Profondes (consomment une entité) — routées au Lot V2.
  CASE_DETAIL:    'caseDetail',
  SESSION_VIEW:   'sessionView',
  HORSE_SESSIONS: 'horseSessions',
  HORSE_SESSION:  'horseSession',
  SESSION_GLOBAL: 'sessionGlobal',
  LOAN_SESSION:   'loanSession',
});

// Pages SANS PARAM routées V0+1 — l'URL fait foi.
export const VET_PAGE_TO_PATH = Object.freeze({
  [VET_PAGES.HOME]:      '/vet/cases',
  [VET_PAGES.NEW_CASE]:  '/vet/cases/new',
  [VET_PAGES.HORSES]:    '/vet/horses',
  [VET_PAGES.NEW_HORSE]: '/vet/horses/new',
  [VET_PAGES.SESSIONS]:  '/vet/sessions',
  [VET_PAGES.LOANS]:     '/vet/loans',
  [VET_PAGES.ACCOUNT]:   '/vet/account',
  [VET_PAGES.FEEDBACK]:  '/vet/feedback',
});

export const VET_PATH_TO_PAGE = Object.freeze(
  Object.fromEntries(Object.entries(VET_PAGE_TO_PATH).map(([page, path]) => [path, page]))
);

// Pages PROFONDES encore pilotées par `vetProPage` (state) → override de rendu.
// Lot V2b : les 4 dernières (horseSessions/horseSession/sessionGlobal/loanSession)
// sont ROUTÉES → set VIDE. FIN DE LA COEXISTENCE AU RENDU : VetProRoot n'a plus de
// switch profond, tout passe par <Routes>. (L'infra state vetProPage/coerceLanding/
// store v3 reste en place — retrait prouvé-mort = Lot V5.)
export const DEEP_VET_PAGES = Object.freeze(new Set([]));

// Whitelist runtime — toutes les valeurs `vetProPage` valides ([useVetProState-
// whitelist-pages]). Une valeur hors set (typo, page retirée, localStorage
// corrompu) doit retomber sur l'atterrissage plutôt que sur un écran blanc.
export const VALID_VET_PAGES = Object.freeze(new Set(Object.values(VET_PAGES)));

/**
 * resolveActiveVetPage — onglet AppNav actif pour l'URL /vet/* courante (pages
 * sans param ; défaut 'sessions', atterrissage). Les pages profondes ne sont pas
 * des onglets → l'appelant passe alors `vetProPage` directement.
 */
export function resolveActiveVetPage(pathname) {
  if (VET_PATH_TO_PAGE[pathname]) return VET_PATH_TO_PAGE[pathname];
  // Routes cas paramétrées (V2a) → onglet « Mes cas » (home). `/vet/cases/new` est
  // déjà capté ci-dessus (exact) → aucune collision avec `:caseId`.
  if (matchPath('/vet/cases/:caseId/sessions/:sessionId', pathname)) return VET_PAGES.HOME;
  if (matchPath('/vet/cases/:caseId', pathname)) return VET_PAGES.HOME;
  // Routes profondes paramétrées (V2b) → onglet de leur section. (`/vet/horses/new`
  // et `/vet/sessions`/`/vet/loans` nus sont captés en exact ci-dessus.)
  if (matchPath('/vet/horses/:horseId/sessions/:sessionId', pathname)) return VET_PAGES.HORSES;
  if (matchPath('/vet/horses/:horseId/sessions', pathname)) return VET_PAGES.HORSES;
  if (matchPath('/vet/sessions/:sessionId', pathname)) return VET_PAGES.SESSIONS;
  if (matchPath('/vet/loans/:loanSessionId', pathname)) return VET_PAGES.LOANS;
  return VET_PAGES.SESSIONS;
}
